import {Injectable} from '@angular/core';
import {GraphHelperService} from '../../diagram-render/helpers/graph-helper.service';
import {DiagramJsonHandlerService} from '../../diagram-data/diagram-json-handler.service';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute} from '@angular/router';
import {timer} from 'rxjs';
import {RequestsService} from '../../core/requests/requests.service';
import * as _ from 'lodash';
import {PopupManagerService} from '../../core/services/popup-manager.service';
import {SpinnerService} from '../../core/spinner/spinner.service';
import {HttpClient} from '@angular/common/http';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../editor-interface/shared/models/diagram-version.constant';
import {PrintCallbackOptions} from 'src/app/editor-interface/dialogs/confirm-print-dialog/models/confirm-print-dialog-data.interface';
import {ConfigService, SnackBarService} from '@q9elements/ui-kit/common';
import {SimpleImagePreloadService} from 'src/app/core/services/simple-image-preload.service';
import {DiagramTypeConfigService} from '../../core/services/diagram-type-config.service';

declare let mxUtils;
declare let mxPrintPreview;
declare let mxRectangle;

@Injectable({
  providedIn: 'root'
})
export class GraphExportService {
  constructor(
    private graphHelper: GraphHelperService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private editorRegistrySvc: EditorRegistryService,
    private preloadImageSvc: SimpleImagePreloadService,
    private snackBarSvc: SnackBarService,
    private translateSvc: TranslateService,
    private route: ActivatedRoute,
    private requestsSvc: RequestsService,
    private popupSvc: PopupManagerService,
    private spinnerSvc: SpinnerService,
    private http: HttpClient,
    private diagramTypeConfigService: DiagramTypeConfigService
  ) {}

  images;
  graph;
  globalCss;

  printPrepare() {
    this.getGlobalCss();
    const isEmpty = this.graphHelper.all().length === 0;

    if (isEmpty) {
      return this.snackBarSvc.error(this.translateSvc.instant('POPUPS.NOTHING_PRINT'));
    }

    this.graph = this.editorRegistrySvc.getEditorUI().editor.graph;
    this.graph.setSelectionCells([]);

    const images = _.filter(this.diagramJsonSvc.get().images, item => {
      return item && !item.deleted && item.imageResized;
    });

    this.images = images;

    if (images.length) {
      this._getNewImageUrls(images)
        .then(urls => this._replaceOldImages(urls))
        .then(urls => this._replaceAndStart(urls));
    } else {
      return this.popupSvc.openConfirmPrintDialog(this, this.startMxPrint);
    }
  }

  private _getNewImageUrls(images) {
    this.spinnerSvc.show();
    const uuids = images.map(image => image.uuid);

    return this.requestsSvc.addToQueue(
      {
        isApi: true,
        method: 'GET',
        url:
          '/diagram/' +
          this.diagramJsonSvc.get().diagramId +
          '/state/' +
          this.stateParams.v +
          '/image/resized/signedurl?images=' +
          uuids.join(',')
      },
      true
    );
  }

  private _replaceOldImages(urls) {
    return Promise.all(urls.map(url => this.preloadImageSvc.preloadImage(url).toPromise()));
  }

  private _replaceAndStart(urls) {
    this.images.forEach((image, index) => {
      const cell = this.graph.model.getCell(+image.mxKey);

      this.graph.setCellStyles('image', urls[index], [cell]);
    });
    this.spinnerSvc.hide();

    return this.popupSvc.openConfirmPrintDialog(this, this.startMxPrint);
  }

  startMxPrint(opts: PrintCallbackOptions) {
    const editorUI = this['context'].editorRegistrySvc.getEditorUI();
    const graph = editorUI.editor.graph;

    opts.addSFAHeader =
      this['context'].diagramTypeConfigService.generalSettings().addSFAHeader && opts.addNames;
    // mxGraph standard sizing for A4 paper is not suitable for some reason, here is adjusted version:
    const pageFormat = opts.portrait
      ? new mxRectangle(0, 0, 709, 1000)
      : new mxRectangle(0, 0, 1032, 700);

    pageFormat.height -= opts.addSFAHeader ? 187 : 32;
    const scale = mxUtils.getScaleForPageCount(1, graph, pageFormat);

    pageFormat.height += opts.addSFAHeader ? 187 : 32;
    const preview = new mxPrintPreview(
      graph,
      scale,
      pageFormat,
      0,
      0,
      opts.addSFAHeader ? 173 : 18
    );

    preview.printOverlays = true;
    preview.title = '';
    const wnd = preview.open();

    this['context'].modifyDoc(wnd.document, opts);
    this['context'].spinnerSvc.show();
    timer(350).subscribe(() => {
      wnd.print();
      preview.close();
      graph.refresh();
      this['context'].spinnerSvc.hide();
    });
  }

  getGlobalCss() {
    const links = document.getElementsByTagName('link');

    let globalStylesRef;

    for (let i = 0; i < links.length; i++) {
      if (links[i].getAttribute('href')?.slice(0, 6) === 'styles') {
        globalStylesRef = links[i].getAttribute('href');
        this.http
          .get(globalStylesRef, {responseType: 'text'})
          .subscribe(css => (this.globalCss = css));
      }
    }
  }

  modifyDoc(doc, opts) {
    const _versionText = () => {
      const isPro = this.diagramJsonSvc.isPro();
      const state = this.stateParams.v;

      if (!isPro || !state) {
        return '';
      }

      switch (state) {
        case DIAGRAM_VERSION_STATES_ENUM.DRAFT:
          return 'Draft version of ';

        case DIAGRAM_VERSION_STATES_ENUM.READY:
          return 'Ready to release version of ';

        case DIAGRAM_VERSION_STATES_ENUM.MASTER:
          return 'Master version of ';

        case DIAGRAM_VERSION_STATES_ENUM.ARCHIVE:
          return 'Archived version of ';

        default:
          return '';
      }
    };

    opts = opts || {};
    const div = doc.body.getElementsByTagName('div')[0];

    if (!div) {
      return;
    }

    const diagramDiv = div.firstChild;

    const styleEl = doc.createElement('style');
    const activityStyles = `
      .q9-activity-shape,
      app-activity-shape,
      .q9-activity-shape .fill,
      app-activity-shape .q9-activity-shape div.default-mode,
      app-activity-shape .q9-activity-shape .text,
      app-activity-shape .q9-activity-shape .text-content {
        height: inherit !important;
      }
      .resources {
        position: absolute;
        height: auto !important;
      }
      .resources-sfa {
        height: auto !important;
      }
    `;

    mxUtils.write(styleEl, activityStyles);

    if (opts.watermark) {
      const watermark = doc.createElement('img');

      watermark.src = `${ConfigService.environment.STATIC_FILES_URL}/icons/elements-logo-colour.svg`;
      watermark.className = 'el-watermark';
      watermark.style.left = diagramDiv.offsetWidth / 2 - 200 + 'px';
      watermark.style.top = diagramDiv.offsetHeight / 2 - 80 + 'px';
      doc.body.appendChild(watermark);
    }

    if (opts.addNames) {
      if (opts.addSFAHeader) {
        const sfaHeaderEl = document.querySelector('.sfa-header-container');
        const headerHtml = mxUtils.getOuterHtml(sfaHeaderEl);

        diagramDiv.insertAdjacentHTML('afterbegin', headerHtml);
        diagramDiv.classList.add('printing');
      } else {
        const names = this.diagramJsonSvc.namings;
        const namesText =
          _versionText() +
          'Diagram "' +
          names.diagramName +
          '" at Level ' +
          names.levelNumber +
          ' of file: "' +
          names.mapName +
          '"';
        const nameDiv = doc.createElement('div');

        nameDiv.className = 'el-print-header';
        mxUtils.write(nameDiv, namesText);
        diagramDiv.appendChild(nameDiv);
      }
    }

    if (opts.footer) {
      const footer = doc.createElement('div');
      const footerText = 'Powered by Elements.cloud';

      footer.className = 'el-print-footer';
      mxUtils.write(footer, footerText);
      diagramDiv.appendChild(footer);
    }

    doc.head.appendChild(styleEl);
  }

  get stateParams() {
    return this.route.snapshot.queryParams;
  }
}
